import { FC, useMemo } from "react";
import { generateWorkflowsResourceFilter } from "./utils/helpers/generateWorkflowsResourceFilter";
import { isIncludingReadOnlyAccounts } from "./utils/helpers/isIncludingReadOnlyAccounts";
import { getUniqueAccountFromResources } from "./utils/helpers/getUniqueAccountFromResources";
import {
  ResourceFiltersWithAction,
  ResourceType,
} from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { useAppAbility } from "@/services/permissions";
import { getFirstParentInHierarchyResourceType } from "@/utils/helpers/resources/getFirstParentInHierarchyResourceType";
import { liveUsageMgmtAccountsWorkflowDataSelector } from "@/store/live-usage-mgmt/selectors/accounts-data/liveUsageMgmtAccountsWorkflowDataSelector";
import { liveUsageMgmtAccountAllReadOnlySelector } from "@/store/live-usage-mgmt/selectors/store-selectors/liveUsageMgmtAccountAllReadOnlySelector";
import { useCreateWorkflow } from "@/components/pages/live-usage-mgmt/utils/hooks/useCreateWorkflow.hook";
import { LiveResourcesCreateWorkflow } from "@/components/pages/live-usage-mgmt/components/common/LiveResourcesCreateWorkflow";
import { selectedResourcesByMasterIdSelector } from "@/store/resources-grid/selectors/grid/selection/selectedResourcesByMasterIdSelector";
import { isResourceKubernetes } from "@/store/resources-grid/utils/helpers/isResourceKubernetes";

interface LiveUsageMgmtCreateWorkflowToolbarActionProps {
  resourceType: ResourceType;
  masterGridId: string;
}

export const LiveUsageMgmtCreateWorkflowToolbarAction: FC<
  LiveUsageMgmtCreateWorkflowToolbarActionProps
> = ({ resourceType, masterGridId }) => {
  const { cannot } = useAppAbility();
  const canNotCreateSchedule = cannot("create", "schedule");

  const accountsWorkflowData = useAppSelector((state) =>
    liveUsageMgmtAccountsWorkflowDataSelector(state, resourceType),
  );

  const selectedResources = useAppSelector((state) =>
    selectedResourcesByMasterIdSelector(state, masterGridId),
  );

  const includesReadOnly = useMemo(
    () => isIncludingReadOnlyAccounts(selectedResources),
    [selectedResources],
  );

  const allSelectedReadOnlyAccount = useMemo(
    () =>
      selectedResources?.every((resource) => {
        if (!resource) {
          return true;
        }

        if (isResourceKubernetes(resource)) {
          return true;
        } else {
          return resource?.account?.provider_access_type === "read";
        }
      }),
    [selectedResources],
  );

  const allLiveResourcesAccountsReadOnly = useAppSelector(
    liveUsageMgmtAccountAllReadOnlySelector,
  );

  const disableActionItems =
    (!!selectedResources?.length && includesReadOnly) ||
    allLiveResourcesAccountsReadOnly;

  const tooltipTitle = useMemo(() => {
    if (
      (!!selectedResources?.length && allSelectedReadOnlyAccount) ||
      allLiveResourcesAccountsReadOnly
    ) {
      return disabledActionItemsTooltipTitleWhenOnlyReadOnly;
    }

    if (!!selectedResources?.length && includesReadOnly) {
      return disabledActionItemsTooltipTitle;
    }

    return "";
  }, [
    selectedResources?.length,
    allSelectedReadOnlyAccount,
    allLiveResourcesAccountsReadOnly,
    includesReadOnly,
  ]);

  const data = useMemo(() => {
    if (!accountsWorkflowData || !selectedResources) {
      return null;
    }

    const accounts = getUniqueAccountFromResources(selectedResources);

    const parentResourceType =
      getFirstParentInHierarchyResourceType(resourceType) ?? resourceType;

    const value = selectedResources.map(
      (resource) => resource?.provider_unique_identifier ?? "",
    );

    let filters: ResourceFiltersWithAction[] = generateWorkflowsResourceFilter(
      parentResourceType,
      resourceType,
      value,
    );

    if (!value.length) {
      filters = accountsWorkflowData.filters.filter(
        (filter) => filter.filter.type === parentResourceType,
      );
    }

    return {
      ...accountsWorkflowData,
      filters,
      resourceType,
      accounts: accounts.length ? accounts : accountsWorkflowData.accounts,
    };
  }, [resourceType, selectedResources, accountsWorkflowData]);

  const createWorkflow = useCreateWorkflow(data);

  if (!data || canNotCreateSchedule) {
    return null;
  }

  return (
    <LiveResourcesCreateWorkflow
      disableActionItems={disableActionItems}
      actionItemsTooltipTitle={tooltipTitle}
      onItemClick={createWorkflow}
    />
  );
};

const disabledActionItemsTooltipTitle = `Please select only resources from Read/Write accounts to enable the \"Take Action\" option.`;
const disabledActionItemsTooltipTitleWhenOnlyReadOnly =
  "All available resources are from read-only accounts. You can only create Notify Only workflow for them";
